<template>
  <v-card :loading="assetStatus === 'pending'" width="320" :height="height ?? '200px'">
    <v-img
      v-if="asset"
      :src="hasScope(asset.organization, AuthScope.CAN_VIEW_FILES) ? thumbnailUrl : 'no-image.png'"
      cover
      :height="height ?? '200px'"
    >
      <v-list style="background-color: rgba(0, 0, 0, 0.4) !important" bg-color="surface" theme="dark">
        <v-list-item class="pr-1">
          <template #title> {{ asset.key }} | {{ asset.description }} </template>
          <template #subtitle>
            {{ asset.category.description }} ({{ asset.template.description }})

            <template v-if="prettifiedDistance">
              {{ prettifiedDistance }}
            </template>
          </template>
        </v-list-item>
      </v-list>

      <v-spacer />

      <v-card-actions class="position-absolute" style="bottom: 0; left: 0; right: 0">
        <v-tooltip location="top">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              color="secondary"
              class="custom-hover-secondary-white"
              rounded="sm"
              density="compact"
              variant="flat"
              :aria-label="`${$t('to')} ${$t('assets.asset')}`"
              style="min-width: 40px; min-height: 40px"
              :to="`/assets/${asset._id}`"
              @click="() => emit('close')"
            >
              {{ $t("open") }}
            </v-btn>
          </template>
          <template #default>
            <span>{{ $t("to") }} {{ $t("assets.asset") }}</span>
          </template>
        </v-tooltip>

        <v-hover v-slot="{ isHovering, props: hoverProps }">
          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="{ ...tooltipProps, ...hoverProps }"
                color="white"
                class="custom-hover-secondary-white"
                rounded="sm"
                density="compact"
                variant="flat"
                icon
                :aria-label="`${$t('to')} ${$t('inspections.title')}`"
                style="min-width: 40px; min-height: 40px"
                :to="`/assets/${asset._id}?tab=INSPECTIONS`"
                @click="() => emit('close')"
              >
                <v-badge
                  v-if="statistics?.types[TicketType.INSPECTION]?.workTotal"
                  color="error"
                  :content="statistics?.types[TicketType.INSPECTION]?.workTotal"
                  offset-x="-8"
                  offset-y="-8"
                  style="z-index: 2"
                >
                  <v-icon icon="$inspection" :color="isHovering ? 'white' : 'secondary'" />
                </v-badge>
                <v-icon v-else icon="$inspection" :color="isHovering ? 'white' : 'secondary'" />
              </v-btn>
            </template>

            <template #default>
              <span>{{ $t("to") }} {{ $t("inspections.title") }}</span>
            </template>
          </v-tooltip>
        </v-hover>

        <v-hover v-slot="{ isHovering, props: hoverProps }">
          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="{ ...tooltipProps, ...hoverProps }"
                color="white"
                class="custom-hover-secondary-white"
                rounded="sm"
                density="compact"
                variant="flat"
                icon
                :aria-label="`${$t('to')} ${$t('malfunctions.title')}`"
                style="min-width: 40px; min-height: 40px"
                :to="`/assets/${asset._id}?tab=MALFUNCTIONS`"
                @click="() => emit('close')"
              >
                <v-badge
                  v-if="statistics?.types[TicketType.MALFUNCTION]?.workTotal"
                  color="error"
                  :content="statistics?.types[TicketType.MALFUNCTION]?.workTotal"
                  offset-x="-8"
                  offset-y="-8"
                >
                  <v-icon icon="$malfunction" :color="isHovering ? 'white' : 'secondary'" />
                </v-badge>
                <v-icon v-else icon="$malfunction" :color="isHovering ? 'white' : 'secondary'" />
              </v-btn>
            </template>

            <template #default>
              <span>{{ $t("to") }} {{ $t("malfunctions.title") }}</span>
            </template>
          </v-tooltip>
        </v-hover>

        <v-hover v-slot="{ isHovering, props: hoverProps }">
          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="{ ...tooltipProps, ...hoverProps }"
                color="white"
                class="custom-hover-secondary-white"
                rounded="sm"
                density="compact"
                variant="flat"
                icon
                :aria-label="`${$t('to')} ${$t('actions.title')}`"
                style="min-width: 40px; min-height: 40px"
                :to="`/assets/${asset._id}?tab=ACTIONS`"
                @click="() => emit('close')"
              >
                <v-badge
                  v-if="statistics?.types[TicketType.ACTION]?.workTotal"
                  color="error"
                  :content="statistics?.types[TicketType.ACTION]?.workTotal"
                  offset-x="-8"
                  offset-y="-8"
                >
                  <v-icon icon="$action" :color="isHovering ? 'white' : 'secondary'" />
                </v-badge>
                <v-icon v-else icon="$action" :color="isHovering ? 'white' : 'secondary'" />
              </v-btn>
            </template>

            <template #default>
              <span>{{ $t("to") }} {{ $t("actions.title") }}</span>
            </template>
          </v-tooltip>
        </v-hover>

        <v-spacer />

        <v-hover v-slot="{ isHovering, props: hoverProps }">
          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-if="asset.location?.gps"
                v-bind="{ ...tooltipProps, ...hoverProps }"
                color="white"
                class="custom-hover-secondary-white"
                rounded="sm"
                density="compact"
                variant="flat"
                icon
                :aria-label="`${$t('openMapsAndNavigateToObject')}`"
                style="min-width: 40px; min-height: 40px"
                :href="navigateToDestination(geoPointToLatLng(asset.location.gps))"
                target="_blank"
                @click.stop
              >
                <v-icon icon="directions" :color="isHovering ? 'white' : 'secondary'" />
              </v-btn>
            </template>
            <template #default>
              <span>{{ $t("openMapsAndNavigateToObject") }}</span>
            </template>
          </v-tooltip>
        </v-hover>
      </v-card-actions>
    </v-img>
  </v-card>
</template>

<script setup lang="ts">
const assetStore = useAssetStore()
const authStore = useAuthStore()
const fileStore = useFileStore()
const { hasScope } = useAuthStore()

const props = defineProps<{
  id: string
  distance?: number
  width?: string
  height?: string
  square?: boolean
  bordered?: boolean
  flat?: boolean
}>()

const { id } = toRefs(props)

const emit = defineEmits(["close"])

const thumbnailPending = ref(false)
const thumbnailUrl = ref<string>()

const { data: asset, status: assetStatus } = useLazyAsyncData(
  `single-asset:${id.value}`,
  async () => {
    return assetStore.getOne(id.value, ["statistics"])
  },
  {
    watch: [id],
  },
)

const statistics = computed<TicketUsageStatistic | undefined>(() => {
  if (!asset.value?.statistics) {
    return undefined
  }

  if (!authStore.isSuperAdmin || authStore.isSupplier) {
    return asset.value.statistics.ticket.suppliers[authStore.sourceOrganization!]
  }

  const { suppliers, ...allTicketStatistics } = asset.value.statistics.ticket

  return allTicketStatistics
})

const updateImage = async () => {
  if (!asset.value) {
    return
  }

  const imageId = asset.value.images[0]

  if (!imageId) {
    thumbnailUrl.value = "/no-image.png"
    return
  }

  try {
    thumbnailPending.value = true
    thumbnailUrl.value = await fileStore.getFileUrl(imageId, "/no-image.png", true)
  } finally {
    thumbnailPending.value = false
  }
}

const prettifiedDistance = computed(() => {
  if (!props.distance) {
    return null
  }

  return props.distance < 1000 ? `${props.distance.toFixed(0)} m` : `${(props.distance / 1000).toFixed(1)} km`
})

watch(
  asset,
  async () => {
    if (!asset.value) {
      return
    }

    if (hasScope(asset.value.organization, AuthScope.CAN_VIEW_FILES)) {
      updateImage()
    }
  },
  { deep: true, immediate: true },
)
</script>
